import { KEEP_TASK, REMOVE_LABEL } from "@/shared/taskRowCopy.ts";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence } from "motion/react";
import { type ReactNode, useMemo, useRef, useState } from "react";

import { CanvasPivotPanel } from "@/components/layout/SplitPanel/CanvasPivotPanel.tsx";
import { PanelToolbar } from "@/components/layout/SplitPanel/PanelToolbar.tsx";
import { Loader } from "@/components/loader/Loader.tsx";
import { cardListClass, cardSurfaceClass } from "@/components/threads/shared.tsx";
import { Button } from "@/components/ui/button.tsx";
import { cn } from "@/lib/utils.ts";
import { type UserTaskLabelProposal } from "@/types/userTasks.ts";
import { userTaskQueries, useAnswerLabelProposal } from "./api.ts";
import { byStatus } from "@/shared/userTaskGrouping.ts";
import { useAnchoredScroll } from "./useAnchoredScroll.ts";
import { Archive, CircleCheck, ListTodo, type LucideIcon } from "lucide-react";
import { CelebrationOverlay } from "@/components/Celebration/CelebrationOverlay.tsx";
import { type TaskEmptyKind, taskEmptyState } from "@/shared/taskEmptyState.ts";
import { useListCleared } from "@/shared/useListCleared.ts";
import { useFlashBackstop } from "@/shared/userTaskFlash.ts";
import {
  allTaskFilters,
  chosenFilter,
  DEFAULT_FILTER,
  OPEN_FILTER,
  narrowToFilter,
  shownProjectIds,
  taskFilters,
  taskIndicatorParts,
} from "@/shared/taskFilterLabels.ts";
import type { PanelControlAxes, PanelControlDimension } from "@/views/Settings/panelControls.tsx";
import {
  useSurfacePreferenceProperty,
  useUpdateSurfacePreferences,
} from "@/components/preferences/usePreferences.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import {
  asTasksViewMode,
  TASKS_VIEW_MODE_LABEL,
  TASKS_VIEW_MODES,
} from "@/shared/taskViewModes.ts";
import { TASKS_VIEW_MODE_DEFAULT } from "@/types/userPreferences.ts";
import { CompactTaskRow } from "./CompactTaskRow.tsx";
import { MirrorControl } from "./MirrorControl.tsx";
import { TaskListMenu } from "./TaskListMenu.tsx";
import { RowButton } from "./RowButton.tsx";
import { UserTaskRow } from "./UserTaskRow.tsx";

/**
 * A label okthx proposes, in Suggested with the suggested tasks: Keep makes it
 * a label and labels its tasks; Dismiss and it is not offered again.
 */
const ProposalCard = ({ proposal }: { proposal: UserTaskLabelProposal }) => {
  const answer = useAnswerLabelProposal();
  return (
    <li
      className={cn(cardSurfaceClass, "flex flex-col gap-1.5")}
      data-label-proposal={proposal.name}
    >
      <span className="text-sm">
        Label {proposal.tasks.length} tasks as &ldquo;{proposal.name}&rdquo;?
      </span>
      <ul className="flex flex-col gap-0.5 text-xs text-muted-foreground">
        {proposal.tasks.map((task) => (
          <li key={task.id}>{task.title}</li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <RowButton
          label={KEEP_TASK}
          statsigName="tasks-label-proposal-keep"
          disabled={answer.isPending}
          onClick={() => answer.mutate({ labelId: proposal.id, answer: "keep" })}
        />
        <RowButton
          label={REMOVE_LABEL}
          statsigName="tasks-label-proposal-dismiss"
          disabled={answer.isPending}
          onClick={() => answer.mutate({ labelId: proposal.id, answer: "dismiss" })}
        />
      </div>
    </li>
  );
};

/**
 * This platform's glyph for each kind of empty. The words are shared; these are
 * not — lucide ships a separate package per platform.
 *
 * Only the caught-up one carries the brand colour, at full strength: it is the
 * empty the reader worked for, where the others are a notice about a list and
 * are drawn as quietly as the line under them.
 */
const EMPTY_ICON: Record<TaskEmptyKind, { Icon: LucideIcon; className: string }> = {
  caughtUp: { Icon: CircleCheck, className: "text-primary" },
  noneClosed: { Icon: Archive, className: "opacity-50" },
  otherFilter: { Icon: ListTodo, className: "opacity-50" },
};

/** What the list says when the filter in front of the reader holds nothing. */
const TasksEmptyState = ({ filterKey, anyTasks }: { filterKey: string; anyTasks: boolean }) => {
  const { kind, heading, line } = taskEmptyState(filterKey, anyTasks);
  const { Icon, className } = EMPTY_ICON[kind];
  return (
    <div className="py-12 text-center text-muted-foreground">
      <Icon className={cn("mx-auto mb-3 size-8", className)} aria-hidden />
      <div className="mb-2 text-lg font-medium text-foreground">{heading}</div>
      <div className="text-sm">{line}</div>
    </div>
  );
};

/**
 * The user's task list on the canvas.
 *
 * Deliberately plain: grouped by status, or by label once any task carries
 * one, each row its title, who holds it, its labels, and links to where it came
 * from. The task a `/tasks/:id` link opened always shows, even when the
 * grouping would leave it out.
 */
export const UserTasksCanvasView = ({
  focusTaskId = null,
  accountSwitcher,
  leading,
}: {
  focusTaskId?: string | null;
  accountSwitcher?: ReactNode;
  leading?: ReactNode;
}) => {
  const { data, isLoading, isError, refetch } = useQuery(userTaskQueries.list());
  const tasks = data?.tasks ?? [];
  const proposals = data?.proposals ?? [];
  const [filter, setFilter] = useState<string>(DEFAULT_FILTER);
  // Answering a task reorders the list under the reader; this holds the row
  // they are looking at still while it happens.
  const listRef = useRef<HTMLDivElement>(null);
  // The chosen filter is resolved here rather than held in an effect: a group
  // can empty out while it is selected — the last task answered — and a filter
  // naming nothing would show an empty list with no way back but the pills.
  const { options, rows, focusShown, chosen } = useMemo(() => {
    const listed = data?.tasks ?? [];
    const withTasks = byStatus(listed).filter((group) => group.tasks.length > 0);
    const projectIds = shownProjectIds(data?.labels, listed);
    const options = data ? taskFilters(withTasks, projectIds.size) : allTaskFilters();
    const chosen = chosenFilter(options, filter);
    const shown = narrowToFilter(chosen, withTasks, projectIds);
    return {
      options,
      rows: shown.flatMap((group) =>
        group.tasks.map((task) => ({ task, group: taskIndicatorParts(task) })),
      ),
      focusShown: shown.some((group) => group.tasks.some((t) => t.id === focusTaskId)),
      chosen,
    };
  }, [data, filter, focusTaskId]);
  /**
   * The task the user arrived on, once it has actually been in the list.
   *
   * The lone row above exists so a link to a task the filter hides is not a
   * dead end. It is an arrival affordance, and answering the task is the end
   * of the arrival — without this, marking a focused task Done drops it from
   * the filter and the escape hatch pins it straight back at the top of Open
   * tasks wearing its new Closed label. The route keeps naming it, so it
   * stays there.
   *
   * Recorded during render rather than in an effect, as the filter below is:
   * the value is read in the same pass, and React re-runs this render with it
   * before committing.
   */
  const [arrivedOn, setArrivedOn] = useState<string | null>(null);
  if (focusTaskId && focusShown && arrivedOn !== focusTaskId) setArrivedOn(focusTaskId);

  /**
   * The reader has just cleared their open list, which is the one arrival worth
   * marking. The same glow-and-volley onboarding plays, over the words that
   * have taken the list's place rather than behind a veil — a veil would be
   * covering a screen that has nothing to swap.
   *
   * Only the open filter: reaching the end of Closed is not an achievement, and
   * neither is a project filter the reader emptied by picking a different one.
   */
  const [celebrating, setCelebrating] = useState(false);
  // The list is mounted only while it is in front, so the flag's backstop
  // counts whenever this is.
  useFlashBackstop(true);
  useListCleared({
    remaining: rows.length,
    enabled: chosen.key === OPEN_FILTER && !isLoading,
    onCleared: () => setCelebrating(true),
  });

  // `rows` is memoised on the query's data, so it is a new array exactly when
  // the list has something new to draw — the same signal the card stack's
  // arrival fade waits for.
  useAnchoredScroll(listRef, rows);
  // A task a link opened that the filter leaves out shows on its own above the rest.
  const focusedAlone =
    focusShown || arrivedOn === focusTaskId ? undefined : tasks.find((t) => t.id === focusTaskId);
  // Adjusted during render rather than in an effect, which the compiler reads
  // as a cascading render. The memo above already fell back for this frame, so
  // writing it back only stops a group regaining a task from silently
  // re-applying a filter the user watched fall away. Guarded on `data`:
  // until the tasks arrive nothing is known to hold anything, and writing that
  // back would make a filter the user never picked their choice for the session.
  if (data && filter !== chosen.key) setFilter(chosen.key);

  const view = asTasksViewMode(useSurfacePreferenceProperty("tasks", "viewMode"));
  const updateTaskPreferences = useUpdateSurfacePreferences("tasks");
  const compact = view === "lines";

  const filterAxis: PanelControlDimension = {
    label: "Filter",
    value: chosen.key,
    onChange: setFilter,
    options: options.map((f) => ({ value: f.key, label: f.label })),
    defaultValue: DEFAULT_FILTER,
    display: "segmented",
    statsigName: "tasks-filter",
  };
  // Behind the sliders with the page's other standing choices rather than
  // behind a glyph of its own: it says how the list is drawn, not what it
  // holds, and the row's width belongs to the question the reader actually
  // came with.
  const viewAxis: PanelControlDimension = {
    label: "View",
    value: view,
    // Narrowed rather than asserted: the axis speaks in strings, and a mode
    // this build does not offer must not reach the preference.
    onChange: (next) => {
      const mode = TASKS_VIEW_MODES.find((offered) => offered === next);
      if (!mode) return;
      void updateTaskPreferences({ viewMode: mode }).catch(() =>
        errorToast("Couldn't save that view. Please try again."),
      );
    },
    options: TASKS_VIEW_MODES.map((mode) => ({
      value: mode,
      label: TASKS_VIEW_MODE_LABEL[mode],
    })),
    defaultValue: TASKS_VIEW_MODE_DEFAULT,
    statsigName: "tasks-view",
  };
  // Spelled out on every width, as Connectors and Routines spell theirs: the
  // row scrolls inside itself on a phone, and a filter behind a glyph is a
  // filter the reader does not know they have on.
  const controls: PanelControlAxes = { filters: [filterAxis] };

  let body: ReactNode;
  if (isLoading) {
    body = <Loader className="py-8" />;
  } else if (isError) {
    body = (
      <div className="flex flex-col items-start gap-2 p-4 text-sm text-muted-foreground">
        Couldn't load your tasks.
        <Button
          variant="outline"
          size="sm"
          onClick={() => void refetch()}
          data-statsig-name="tasks-list-retry"
        >
          Try again
        </Button>
      </div>
    );
  } else {
    body = (
      // The focused task's glow reaches past its card on every side and this
      // scroller clipped flush to it, cutting the top card's glow along its top
      // edge. The padding buys the room; the negative margins give the space
      // back, so the list sits exactly where it did. 16px because that is where
      // the glow measures out — it falls to an imperceptible 2/255 against the
      // page there and to nothing by 18px, so a wider gutter buys only waste.
      <div
        ref={listRef}
        className="-mx-5 -mt-4 flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 pb-4 pt-4"
      >
        {focusedAlone && (
          <ul className={cardListClass} data-task-group="focused">
            {compact ? (
              <CompactTaskRow
                task={focusedAlone}
                group={taskIndicatorParts(focusedAlone)}
                focused
              />
            ) : (
              <UserTaskRow task={focusedAlone} group={taskIndicatorParts(focusedAlone)} focused />
            )}
          </ul>
        )}
        {focusTaskId && !focusShown && !focusedAlone && (
          <p className="text-sm text-muted-foreground">That task is no longer on your list.</p>
        )}
        {proposals.length > 0 && (
          <section className="flex flex-col gap-2" data-task-group="proposals">
            <h2 className="text-xs font-medium text-muted-foreground">Proposed labels</h2>
            <ul className={cardListClass}>
              {proposals.map((proposal) => (
                <ProposalCard key={proposal.id} proposal={proposal} />
              ))}
            </ul>
          </section>
        )}
        {/* One list, no section headers: each card names its own group, so the
            rows keep one rhythm instead of a band of heading between every
            handful of them. The same arrangement the native screen uses. */}
        {/* `cardListClass`, the same pitch Routines, Connectors and Skills
            space their cards by, so one card sits the same on four pages of
            one app. One pitch for both densities: a compact line is a card of
            its own, drawn at a row's height rather than a card's, and the
            thread list spaces its compact rows this way at the width they
            stay white.

            `relative` on top of it, because a row on its way out is taken out
            of the flow and needs this to position against. An out-of-flow row
            takes no part in the gap either, so the row below closes up by
            sliding, which is what the exit animation wants. */}
        <ul className={cn(cardListClass, "relative")} data-task-group="all">
          <AnimatePresence initial={false}>
            {rows.map(({ task, group }) =>
              compact ? (
                <CompactTaskRow
                  key={task.id}
                  task={task}
                  group={group}
                  focused={task.id === focusTaskId}
                />
              ) : (
                <UserTaskRow
                  key={task.id}
                  task={task}
                  group={group}
                  focused={task.id === focusTaskId}
                />
              ),
            )}
          </AnimatePresence>
        </ul>
        {/* Inside the scroller rather than in place of it, so the list stays
            mounted as its last row leaves and that row gets its exit. */}
        {rows.length === 0 && proposals.length === 0 && !focusedAlone && (
          <TasksEmptyState filterKey={chosen.key} anyTasks={tasks.length > 0} />
        )}
      </div>
    );
  }

  return (
    <CanvasPivotPanel
      // The heading says which filter is in effect rather than naming the tab
      // again: the toolbar under it shows every filter, and nothing else said
      // which of them the list in front of the user had been narrowed to.
      title={chosen.label}
      leading={leading}
      accountSwitcher={accountSwitcher}
      toolbar={
        <PanelToolbar
          controls={controls}
          subject="tasks"
          statsigName="tasks-canvas-filter"
          trailing={<TaskListMenu view={viewAxis} />}
        />
      }
    >
      {/* `mb-3`, the pitch the cards below keep: the panel stacks its children
          with no gap of its own, and the scroller's own `-mt-4` cancels the
          `pt-4` it carries for the focus glow — so without this the mirror
          sits flush against the first card. */}
      <div className="mb-3 flex items-center">
        <MirrorControl />
      </div>
      {body}
      {/* Draws nothing here: the glow and its canvas are fixed to the viewport,
          so the volley covers the panel and the thread beside it alike. Mounted
          only while it is owed, because mounting it is the celebration. */}
      {celebrating && <CelebrationOverlay onDone={() => setCelebrating(false)} />}
    </CanvasPivotPanel>
  );
};
